/**
 * `RowAddress` — a live row's data name (`AddressOf`, `../ids/address-of.ts`):
 * the adapter from a `ModelRow` and its variant (a computed source's relative
 * `localPath`, an alias container, a renderer painting its parent) to the
 * structural `AddressOf.AddressableRow` the classifier reads. The classifier
 * itself names no row class; this is the one place the row variants meet it.
 */

import type { Option } from "../core/option.ts";
import { AddressOf } from "../ids/address-of.ts";
import { isSyntheticDefaultRendererId } from "../ids/row-local-id.ts";
import type { ModelRow } from "./model-row.ts";
import { AliasRow } from "./variants/alias-row.ts";
import { ComputedDataSource } from "./variants/computed-data-source.ts";
import { RendererRow } from "./variants/renderer-row.ts";

/** Values that classify a live row's data name: `of`, `forRow`. */
export namespace RowAddress {
  /** Adapt a live `ModelRow` to the structural surface, then `AddressOf.resolve`. */
  export function of(row: ModelRow): AddressOf.Result {
    return AddressOf.resolve(forRow(row));
  }

  /**
   * Adapt a live `ModelRow` (and, lazily, its ancestor chain + renderer source)
   * to the structural `AddressableRow` surface.
   */
  export function forRow(row: ModelRow): AddressOf.AddressableRow {
    return {
      serial: row.id.serial,
      key: row.id.key,
      lookupKey: row.lookupKey,
      localPath: row instanceof ComputedDataSource ? row.localPath ?? null : null,
      parent: row.parent != null ? forRow(row.parent) ?? null : null,
      rendererSource: rendererSourceOf(row),
      isContainer: row instanceof AliasRow,
    };
  }

  /**
   * Resolve a renderer row's source row (the row it paints). A synthetic
   * default renderer (`virtual(r_<parentHex>|renderer)`) is hydrated as a tree
   * child of its source (`sheet.ts:1032`, `raw-sheet.ts:427`), and authored
   * renderer children are declared `placement: "child"` under the host they
   * paint — so the tree parent is the source in both cases. Non-renderer rows
   * (and a renderer somehow at the sheet root) have no delegation source.
   */
  function rendererSourceOf(row: ModelRow): Option<AddressOf.AddressableRow> {
    if (!(row instanceof RendererRow)) return null;
    const parent = row.parent;
    if (parent == null) return null;
    // `isSyntheticDefaultRendererId` is referenced to document the delegation
    // target: for synthetic default renderers the tree parent equals the
    // renderer id's `virtual` parent (`virt.parentId`), the very handle
    // `sheet-cell-accessor.ts:330` delegates scalar reads to.
    void isSyntheticDefaultRendererId;
    return forRow(parent);
  }
}
